import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const h = () => setUser(false);
    window.addEventListener("auth-expired", h);
    return () => window.removeEventListener("auth-expired", h);
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  const can = (action) => {
    const role = user?.role;
    if (!role) return false;
    if (action === "delete") return ["admin", "manager", "sales"].includes(role);
    if (action === "admin") return role === "admin";
    if (action === "import") return ["admin", "manager", "sales"].includes(role);
    if (action === "import_update") return ["admin", "manager"].includes(role);
    if (action === "import_rollback") return role === "admin";
    return ["admin", "manager", "sales", "collaborator"].includes(role); // write
  };

  return (
    <AuthContext.Provider value={{ user, error, login, logout, refresh, can, setError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
